/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

export type ToastType = "success" | "error" | "info";

export interface Toast {
  /** Where the triggering click happened, so the toast can fly in from it. */
  origin: { x: number; y: number } | null;
  id: string;
  type: ToastType;
  message: string;
  duration: number;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (type: ToastType, message: string, duration?: number) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const MAX_TOASTS = 4;
const DEFAULT_SUCCESS_DURATION = 4000;
const DEFAULT_ERROR_DURATION = 6000;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // The most recent pointer press, kept briefly: a toast that follows a click flies in from it;
  // keyboard-triggered and programmatic toasts (stale point) use the plain entrance.
  const lastPointer = useRef<{ x: number; y: number; at: number } | null>(null);
  useEffect(() => {
    const remember = (event: PointerEvent) => {
      lastPointer.current = { x: event.clientX, y: event.clientY, at: performance.now() };
    };
    window.addEventListener("pointerdown", remember, { capture: true, passive: true });
    return () => window.removeEventListener("pointerdown", remember, { capture: true });
  }, []);

  const dismissToast = useCallback((id: string) => {
    const timeout = timeoutRefs.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutRefs.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (type: ToastType, message: string, duration?: number) => {
      const id = `${Date.now()}-${Math.random()}`;
      const defaultDuration =
        type === "success" ? DEFAULT_SUCCESS_DURATION : DEFAULT_ERROR_DURATION;
      const finalDuration = duration ?? defaultDuration;

      const pointer = lastPointer.current;
      const origin =
        pointer !== null && performance.now() - pointer.at < 2000
          ? { x: pointer.x, y: pointer.y }
          : null;
      const newToast: Toast = {
        id,
        type,
        message,
        duration: finalDuration,
        origin,
      };

      setToasts((prev) => {
        const updatedToasts = [newToast, ...prev];
        if (updatedToasts.length > MAX_TOASTS) {
          const removed = updatedToasts.pop();
          if (removed) {
            const timeout = timeoutRefs.current.get(removed.id);
            if (timeout) {
              clearTimeout(timeout);
              timeoutRefs.current.delete(removed.id);
            }
          }
        }
        return updatedToasts;
      });

      const timeout = setTimeout(() => {
        dismissToast(id);
      }, finalDuration);

      timeoutRefs.current.set(id, timeout);
    },
    [dismissToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
